import React, { useState, useEffect } from "react";
import {
  Row,
  Col,
  Tag,
  Modal,
  Dropdown,
  Menu,
  Button,
  Input,
  Table,
  Select,
  DatePicker,
  Card,
  Tooltip,
  Alert,
  message,
  Spin,
  Progress,
  Badge,
  Checkbox,
  Popover,
  Avatar,
} from "antd";
import {
  FiMoreVertical,
  FiFilter,
  FiDownload,
  FiRefreshCw,
  FiAlertTriangle,
  FiCheckCircle,
  FiClock,
  FiPlay,
  FiSearch,
  FiUsers,
  FiUserCheck,
  FiUserX,
  FiTrendingUp,
  FiTrendingDown,
  FiDollarSign,
  FiZap,
  FiMail,
  FiTrash2,
  FiLock,
  FiUnlock,
  FiCopy,
  FiActivity,
  FiPercent,
  FiTarget,
  FiAward,
  FiAlertCircle,
} from "react-icons/fi";
import {
  RiEyeFill,
  RiDeleteBin6Line,
  RiBuilding4Line,
  RiCalendarCheckLine,
  RiVipCrownLine,
  RiSpyLine,
} from "react-icons/ri";
import { MdOutlinePersonAddDisabled } from "react-icons/md";
import {
  getAllAdminsPaginated,
  toggleDisableUserAccount,
  deleteUserAccount,
  changeAdminPlan,
  getRefillStatistics,
  manualRefillAdmin,
  bulkManualRefillAdmins,
  triggerRefillJob,
} from "@api/user/superAdmin";
import { useHistory } from "react-router-dom";
import { SuccessNotification, ErrorNotification } from "@components";
import * as XLSX from "xlsx";
import "@assets/css/AdminList.css";

const { Option } = Select;
const { RangePicker } = DatePicker;

// Animated number component
const AnimatedNumber = ({ value, duration = 1000, prefix = "", suffix = "" }) => {
  const [displayValue, setDisplayValue] = useState(0);

  useEffect(() => {
    let start = 0;
    const end = value || 0;
    if (end === 0) {
      setDisplayValue(0);
      return;
    }
    const increment = end / (duration / 16);
    const timer = setInterval(() => {
      start += increment;
      if (start >= end) {
        setDisplayValue(end);
        clearInterval(timer);
      } else {
        setDisplayValue(Math.floor(start));
      }
    }, 16);
    return () => clearInterval(timer);
  }, [value, duration]);

  return <span>{prefix}{displayValue.toLocaleString()}{suffix}</span>;
};

// Risk Indicator Component
const RiskIndicator = ({ admin }) => {
  // Calculate risk score based on various factors
  const calculateRisk = () => {
    let riskScore = 0;
    const factors = [];

    // Inactive for 30+ days (high risk)
    const lastActive = admin.lastActive;
    if (lastActive) {
      const daysSinceActive = Math.floor(
        (Date.now() - new Date(lastActive).getTime()) / (1000 * 60 * 60 * 24)
      );
      if (daysSinceActive > 60) {
        riskScore += 40;
        factors.push("Inactive for 60+ days");
      } else if (daysSinceActive > 30) {
        riskScore += 25;
        factors.push("Inactive for 30+ days");
      } else if (daysSinceActive > 14) {
        riskScore += 10;
        factors.push("Inactive for 2 weeks");
      }
    }

    // Low usage compared to plan limits
    const usagePercent = admin.usagePercent || 0;
    if (usagePercent < 10) {
      riskScore += 30;
      factors.push("Very low product usage");
    } else if (usagePercent < 30) {
      riskScore += 15;
      factors.push("Low product usage");
    }

    // Free plan with no upgrade
    if ((admin.currentPlan || "").toLowerCase() === "free") {
      riskScore += 10;
      factors.push("On free plan");
    }

    // Payment issues
    if (admin.paymentFailed) {
      riskScore += 35;
      factors.push("Payment failed");
    }

    return { score: Math.min(riskScore, 100), factors };
  };

  const { score, factors } = calculateRisk();

  if (score === 0) return null;

  const getRiskColor = () => {
    if (score >= 60) return "#ef4444";
    if (score >= 30) return "#f59e0b";
    return "#64748b";
  };

  const getRiskLabel = () => {
    if (score >= 60) return "High Risk";
    if (score >= 30) return "Medium Risk";
    return "Low Risk";
  };

  return (
    <Tooltip
      title={
        <div>
          <div style={{ fontWeight: 600, marginBottom: 8 }}>Churn Risk Factors:</div>
          {factors.map((f, i) => (
            <div key={i} style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 4 }}>
              <FiAlertCircle size={12} />
              <span>{f}</span>
            </div>
          ))}
        </div>
      }
    >
      <Tag
        style={{
          background: `${getRiskColor()}15`,
          color: getRiskColor(),
          border: `1px solid ${getRiskColor()}30`,
          fontSize: 11,
          padding: "2px 6px",
        }}
      >
        <FiAlertTriangle size={10} style={{ marginRight: 4 }} />
        {getRiskLabel()}
      </Tag>
    </Tooltip>
  );
};

// Quick Preview Popover
const AdminQuickPreview = ({ admin, children }) => {
  const content = (
    <div className="admin-quick-preview">
      <div className="preview-header">
        <Avatar size={48} style={{ background: "#8B5CF6" }}>
          {(admin.name || "U").charAt(0).toUpperCase()}
        </Avatar>
        <div className="preview-info">
          <div className="preview-name">{admin.name || "No Name"}</div>
          <div className="preview-email">{admin.email}</div>
        </div>
      </div>
      <div className="preview-stats">
        <div className="preview-stat">
          <span className="preview-stat-label">Plan</span>
          <Tag className={`admin-plan-tag ${(admin.currentPlan || "free").toLowerCase()}`}>
            {admin.currentPlan || "Free"}
          </Tag>
        </div>
        <div className="preview-stat">
          <span className="preview-stat-label">Generations</span>
          <span className="preview-stat-value">{(admin.generations || 0).toLocaleString()}</span>
        </div>
        <div className="preview-stat">
          <span className="preview-stat-label">Team Size</span>
          <span className="preview-stat-value">{admin.team || 0}</span>
        </div>
        <div className="preview-stat">
          <span className="preview-stat-label">Last Active</span>
          <span className="preview-stat-value">
            {admin.lastActive
              ? new Date(admin.lastActive).toLocaleDateString()
              : "Never"}
          </span>
        </div>
      </div>
      <RiskIndicator admin={admin} />
    </div>
  );

  return (
    <Popover content={content} trigger="hover" placement="right" mouseEnterDelay={0.5}>
      {children}
    </Popover>
  );
};

const AdminList = () => {
  const history = useHistory();
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const [sortBy, setSortBy] = useState("createdAt");
  const [sortOrder, setSortOrder] = useState("desc");
  const [filterPlan, setFilterPlan] = useState(null);
  const [filterStatus, setFilterStatus] = useState(null);
  const [filterDateRange, setFilterDateRange] = useState(null);
  const [changePlanModalVisible, setChangePlanModalVisible] = useState(false);
  const [selectedAdminId, setSelectedAdminId] = useState(null);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [availablePlans] = useState([
    { id: "free", name: "Free", color: "#64748b" },
    { id: "optimizer", name: "Optimizer", color: "#10b981" },
    { id: "wizard", name: "Wizard", color: "#8B5CF6" },
    { id: "maverick", name: "Maverick", color: "#ec4899" },
  ]);

  // Bulk selection state
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [bulkActionLoading, setBulkActionLoading] = useState(false);

  // Stats with revenue metrics
  const [stats, setStats] = useState({
    total: 0,
    active: 0,
    disabled: 0,
    newThisMonth: 0,
    mrr: 0,
    conversionRate: 0,
    churnRisk: 0,
  });

  // Refills Modal State
  const [refillsModalVisible, setRefillsModalVisible] = useState(false);
  const [refillsLoading, setRefillsLoading] = useState(false);
  const [refillsData, setRefillsData] = useState([]);
  const [refillsStats, setRefillsStats] = useState(null);
  const [refillsPagination, setRefillsPagination] = useState({
    current: 1,
    pageSize: 20,
    total: 0,
  });
  const [refillsFilters, setRefillsFilters] = useState({
    plan: null,
    overdueOnly: false,
    recentOnly: false,
    sortBy: "nextAllocationDate",
    sortOrder: "asc",
  });
  const [selectedRefillKeys, setSelectedRefillKeys] = useState([]);
  const [refillActionLoading, setRefillActionLoading] = useState({});

  // Bulk Email Modal
  const [bulkEmailModalVisible, setBulkEmailModalVisible] = useState(false);

  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search);
    const page = parseInt(searchParams.get("page") || "1", 10);
    const pageSize = parseInt(searchParams.get("pageSize") || "10", 10);
    const search = searchParams.get("search") || "";
    const sort = searchParams.get("sortBy") || "createdAt";
    const order = searchParams.get("sortOrder") || "desc";
    const plan = searchParams.get("plan") || null;
    const status = searchParams.get("status") || null;

    setSearchText(search);
    setSortBy(sort);
    setSortOrder(order);
    setFilterPlan(plan);
    setFilterStatus(status);
    setPagination({ current: page, pageSize, total: 0 });

    fetchAdmins(page, pageSize, search, sort, order, { plan, status });
  }, []);

  const fetchAdmins = async (
    page,
    pageSize,
    searchText,
    sortBy = "createdAt",
    sortOrder = "desc",
    filterBy = {}
  ) => {
    setLoading(true);
    try {
      const res = await getAllAdminsPaginated(page, pageSize, searchText);

      const adminData = res.data.admins;
      setAdmins(adminData);
      setPagination((prev) => ({
        ...prev,
        total: res.data.paginationData.totalAdmins,
        current: page,
        pageSize: pageSize,
      }));

      // Calculate stats including revenue metrics
      const activeCount = adminData.filter((a) => !a.disabled).length;
      const disabledCount = adminData.filter((a) => a.disabled).length;

      // Calculate MRR (mock calculation - in production, get from API)
      const planPrices = { free: 0, optimizer: 29, wizard: 79, maverick: 199 };
      const mrr = adminData.reduce((sum, admin) => {
        const plan = (admin.currentPlan || "free").toLowerCase();
        return sum + (planPrices[plan] || 0);
      }, 0);

      // Calculate conversion rate (paid / total)
      const paidCount = adminData.filter(
        (a) => a.currentPlan && a.currentPlan.toLowerCase() !== "free"
      ).length;
      const conversionRate = adminData.length > 0
        ? Math.round((paidCount / adminData.length) * 100)
        : 0;

      // Calculate churn risk count
      const churnRiskCount = adminData.filter((admin) => {
        const lastActive = admin.lastActive;
        if (!lastActive) return true;
        const daysSinceActive = Math.floor(
          (Date.now() - new Date(lastActive).getTime()) / (1000 * 60 * 60 * 24)
        );
        return daysSinceActive > 30;
      }).length;

      setStats({
        total: res.data.paginationData.totalAdmins,
        active: activeCount,
        disabled: disabledCount,
        newThisMonth: res.data.paginationData.newThisMonth || 0,
        mrr,
        conversionRate,
        churnRisk: churnRiskCount,
      });

      // Clear selection when data changes
      setSelectedRowKeys([]);
    } catch (error) {
      console.error("Fetch admins error", error);
      Modal.error({
        title: "Failed to load admin data",
        content: "There was an error loading the admin list. Please try again.",
      });
    } finally {
      setLoading(false);
    }
  };

  // ========== Bulk Actions ==========
  const handleBulkAction = async (action) => {
    if (selectedRowKeys.length === 0) {
      message.warning("Please select at least one admin");
      return;
    }

    const count = selectedRowKeys.length;

    switch (action) {
      case "disable":
        Modal.confirm({
          title: `Disable ${count} accounts?`,
          content: "These accounts will be temporarily disabled and users won't be able to log in.",
          okText: "Disable All",
          okType: "danger",
          onOk: async () => {
            setBulkActionLoading(true);
            try {
              // In production, call bulk API
              for (const id of selectedRowKeys) {
                await toggleDisableUserAccount(id);
              }
              message.success(`${count} accounts disabled`);
              setSelectedRowKeys([]);
              fetchAdmins(pagination.current, pagination.pageSize, searchText, sortBy, sortOrder, {
                plan: filterPlan,
                status: filterStatus,
              });
            } catch (error) {
              message.error("Failed to disable some accounts");
            } finally {
              setBulkActionLoading(false);
            }
          },
        });
        break;

      case "enable":
        Modal.confirm({
          title: `Enable ${count} accounts?`,
          content: "These accounts will be re-enabled.",
          okText: "Enable All",
          onOk: async () => {
            setBulkActionLoading(true);
            try {
              for (const id of selectedRowKeys) {
                await toggleDisableUserAccount(id);
              }
              message.success(`${count} accounts enabled`);
              setSelectedRowKeys([]);
              fetchAdmins(pagination.current, pagination.pageSize, searchText, sortBy, sortOrder, {
                plan: filterPlan,
                status: filterStatus,
              });
            } catch (error) {
              message.error("Failed to enable some accounts");
            } finally {
              setBulkActionLoading(false);
            }
          },
        });
        break;

      case "email":
        setBulkEmailModalVisible(true);
        break;

      case "export":
        const selectedAdmins = admins.filter((a) => selectedRowKeys.includes(a.user || a._id));
        exportAdmins(selectedAdmins);
        break;

      case "refill":
        Modal.confirm({
          title: `Refill credits for ${count} admins?`,
          content: "This will manually refill credits for all selected admins.",
          okText: "Refill All",
          onOk: async () => {
            setBulkActionLoading(true);
            try {
              await bulkManualRefillAdmins(selectedRowKeys);
              message.success(`Credits refilled for ${count} admins`);
              setSelectedRowKeys([]);
              fetchAdmins(pagination.current, pagination.pageSize, searchText, sortBy, sortOrder, {
                plan: filterPlan,
                status: filterStatus,
              });
            } catch (error) {
              message.error("Failed to refill credits");
            } finally {
              setBulkActionLoading(false);
            }
          },
        });
        break;

      default:
        break;
    }
  };

  // ========== Refills Functions ==========
  const fetchRefillsData = async () => {
    setRefillsLoading(true);
    try {
      const response = await getRefillStatistics({
        page: refillsPagination.current,
        limit: refillsPagination.pageSize,
        ...refillsFilters,
      });

      setRefillsData(response.data?.data || []);
      setRefillsStats(response.data?.stats);
      setRefillsPagination((prev) => ({
        ...prev,
        total: response.data?.pagination?.total || 0,
      }));
    } catch (error) {
      console.error("Error fetching refill data:", error);
      message.error("Failed to fetch refill data");
    } finally {
      setRefillsLoading(false);
    }
  };

  const openRefillsModal = () => {
    setRefillsModalVisible(true);
    fetchRefillsData();
  };

  useEffect(() => {
    if (refillsModalVisible) {
      fetchRefillsData();
    }
  }, [refillsFilters, refillsPagination.current, refillsPagination.pageSize]);

  const handleManualRefill = async (adminId) => {
    setRefillActionLoading((prev) => ({ ...prev, [adminId]: true }));
    try {
      const response = await manualRefillAdmin(adminId);
      if (response.data.success) {
        message.success("Admin refilled successfully");
        fetchRefillsData();
      } else {
        message.error(response.data.message || "Refill failed");
      }
    } catch (error) {
      console.error("Error refilling admin:", error);
      message.error(error.response?.data?.message || "Failed to refill admin");
    } finally {
      setRefillActionLoading((prev) => ({ ...prev, [adminId]: false }));
    }
  };

  const handleBulkRefill = async () => {
    if (selectedRefillKeys.length === 0) {
      message.warning("Please select admins to refill");
      return;
    }

    Modal.confirm({
      title: `Refill ${selectedRefillKeys.length} admins?`,
      content: "This will manually refill credits for all selected admins.",
      okText: "Refill All",
      okType: "primary",
      onOk: async () => {
        try {
          const response = await bulkManualRefillAdmins(selectedRefillKeys);
          message.success(response.data.message);
          setSelectedRefillKeys([]);
          fetchRefillsData();
        } catch (error) {
          console.error("Error bulk refilling:", error);
          message.error("Failed to bulk refill admins");
        }
      },
    });
  };

  const handleTriggerJob = async (jobType) => {
    message.loading({ content: `Running ${jobType} refill job...`, key: jobType });
    try {
      const response = await triggerRefillJob(jobType);
      const processed = response.data?.result?.processed || 0;
      const skipped = response.data?.result?.skipped || 0;
      message.success({
        content: `${jobType} refill job completed: ${processed} processed, ${skipped} skipped`,
        key: jobType,
      });
      fetchRefillsData();
    } catch (error) {
      console.error("Error triggering job:", error);
      message.error({ content: `Failed to trigger ${jobType} refill job`, key: jobType });
    }
  };

  const formatRefillDate = (dateString) => {
    if (!dateString) return "-";
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const refillColumns = [
    {
      title: "User",
      key: "user",
      width: 200,
      render: (_, record) => (
        <div className="admin-user-info">
          <div className="admin-user-avatar">
            {(record.name || "U").charAt(0).toUpperCase()}
          </div>
          <div className="admin-user-details">
            <div className="admin-user-name" style={{ cursor: "default" }}>
              {record.name || "No Name"}
            </div>
            <div className="admin-user-email">{record.email}</div>
          </div>
        </div>
      ),
    },
    {
      title: "Plan",
      dataIndex: "currentPlan",
      key: "currentPlan",
      width: 90,
      render: (plan) => (
        <Tag className={`admin-plan-tag ${(plan || "free").toLowerCase()}`}>
          {plan || "Unknown"}
        </Tag>
      ),
    },
    {
      title: "Status",
      key: "status",
      width: 110,
      render: (_, record) => {
        if (record.isOverdue) {
          return (
            <Tooltip title={`${record.daysOverdue} days overdue`}>
              <Tag className="admin-status-tag disabled">
                <FiAlertTriangle size={12} />
                Overdue ({record.daysOverdue}d)
              </Tag>
            </Tooltip>
          );
        }
        return (
          <Tag className="admin-status-tag active">
            <FiCheckCircle size={12} />
            Active
          </Tag>
        );
      },
    },
    {
      title: "Next Allocation",
      dataIndex: "nextAllocationDate",
      key: "nextAllocationDate",
      width: 150,
      render: (date) => formatRefillDate(date),
    },
    {
      title: "Last Refill",
      dataIndex: "lastRefillDate",
      key: "lastRefillDate",
      width: 150,
      render: (date, record) => (
        <div>
          <div>{formatRefillDate(date)}</div>
          {record.daysSinceLastRefill !== null && (
            <div style={{ fontSize: "11px", color: "#64748b" }}>
              {record.daysSinceLastRefill}d ago
            </div>
          )}
        </div>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      width: 100,
      render: (_, record) => (
        <button
          className="admin-action-btn"
          onClick={() => handleManualRefill(record.adminId)}
          disabled={refillActionLoading[record.adminId]}
        >
          {refillActionLoading[record.adminId] ? (
            <Spin size="small" />
          ) : (
            <>
              <FiRefreshCw size={14} />
              Refill
            </>
          )}
        </button>
      ),
    },
  ];

  const refillRowSelection = {
    selectedRowKeys: selectedRefillKeys,
    onChange: (keys) => setSelectedRefillKeys(keys),
    getCheckboxProps: (record) => ({
      disabled: !record.isOverdue,
    }),
  };

  // ========== Admin List Functions ==========
  const handlePaginationChange = (page, pageSize) => {
    const searchParams = new URLSearchParams();
    searchParams.set("page", page);
    searchParams.set("pageSize", pageSize);
    if (searchText) searchParams.set("search", searchText);
    if (sortBy) searchParams.set("sortBy", sortBy);
    if (sortOrder) searchParams.set("sortOrder", sortOrder);
    if (filterPlan) searchParams.set("plan", filterPlan);
    if (filterStatus) searchParams.set("status", filterStatus);

    history.push({ search: searchParams.toString() });
    setPagination({ ...pagination, current: page, pageSize });

    fetchAdmins(page, pageSize, searchText, sortBy, sortOrder, {
      plan: filterPlan,
      status: filterStatus,
      dateRange: filterDateRange,
    });
  };

  const handleSearch = (searchValue) => {
    setSearchText(searchValue);
    setPagination({ ...pagination, current: 1 });

    fetchAdmins(1, pagination.pageSize, searchValue, sortBy, sortOrder, {
      plan: filterPlan,
      status: filterStatus,
      dateRange: filterDateRange,
    });

    const searchParams = new URLSearchParams();
    searchParams.set("page", "1");
    searchParams.set("pageSize", pagination.pageSize);
    if (searchValue) searchParams.set("search", searchValue);
    if (sortBy) searchParams.set("sortBy", sortBy);
    if (sortOrder) searchParams.set("sortOrder", sortOrder);
    if (filterPlan) searchParams.set("plan", filterPlan);
    if (filterStatus) searchParams.set("status", filterStatus);

    history.push({ search: searchParams.toString() });
  };

  const handleAdminView = (id) => {
    history.push(`/super-admin/admin/${id}`);
  };

  const handleToggleUserDisable = (id, currentlyDisabled) => {
    const action = currentlyDisabled ? "enable" : "disable";
    Modal.confirm({
      title: `${currentlyDisabled ? "Enable" : "Disable"} User Account`,
      content: `Are you sure you want to ${action} this account?`,
      okText: currentlyDisabled ? "Enable" : "Disable",
      okType: currentlyDisabled ? "primary" : "danger",
      cancelText: "Cancel",
      onOk: async () => {
        try {
          const response = await toggleDisableUserAccount(id);
          fetchAdmins(
            pagination.current,
            pagination.pageSize,
            searchText,
            sortBy,
            sortOrder,
            {
              plan: filterPlan,
              status: filterStatus,
              dateRange: filterDateRange,
            }
          );
          SuccessNotification(response.data?.message || `Account ${action}d successfully`);
        } catch (err) {
          console.log("Toggle admin disable error", err);
          ErrorNotification(err.response?.data?.error || `Failed to ${action} account`);
        }
      },
    });
  };

  const handleDeleteUserAccount = (id, adminName) => {
    Modal.confirm({
      title: "Delete User Account",
      content: (
        <div>
          <p>Are you sure you want to permanently delete <strong>{adminName || "this user"}</strong>?</p>
          <p style={{ color: "#ff4d4f", fontSize: "12px", marginTop: "8px" }}>
            This will delete all associated data including:
          </p>
          <ul style={{ fontSize: "12px", color: "#666", paddingLeft: "20px" }}>
            <li>All generations and chats</li>
            <li>Custom copilots and prompts</li>
            <li>Subscription and usage data</li>
            <li>Team memberships</li>
          </ul>
          <p style={{ color: "#ff4d4f", fontWeight: "bold", marginTop: "8px" }}>
            This action cannot be undone.
          </p>
        </div>
      ),
      okText: "Delete Permanently",
      okType: "danger",
      cancelText: "Cancel",
      onOk: async () => {
        try {
          const response = await deleteUserAccount({ userId: id });
          fetchAdmins(
            pagination.current,
            pagination.pageSize,
            searchText,
            sortBy,
            sortOrder,
            {
              plan: filterPlan,
              status: filterStatus,
              dateRange: filterDateRange,
            }
          );
          SuccessNotification(response.data?.message || "User deleted successfully");
        } catch (error) {
          ErrorNotification(error.response?.data?.error || "Error deleting user");
        }
      },
    });
  };

  const showChangePlanModal = (adminId) => {
    setSelectedAdminId(adminId);
    setChangePlanModalVisible(true);
  };

  const handleChangePlan = async () => {
    if (!selectedPlan || !selectedAdminId) {
      return;
    }

    setLoading(true);
    try {
      await changeAdminPlan(selectedAdminId, selectedPlan);
      setChangePlanModalVisible(false);
      setSelectedAdminId(null);
      setSelectedPlan(null);

      fetchAdmins(
        pagination.current,
        pagination.pageSize,
        searchText,
        sortBy,
        sortOrder,
        {
          plan: filterPlan,
          status: filterStatus,
          dateRange: filterDateRange,
        }
      );

      SuccessNotification("Plan changed successfully");
    } catch (error) {
      console.error("Error changing plan:", error);
      ErrorNotification("Failed to change plan");
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    const options = { day: "numeric", month: "short", year: "numeric" };
    const date = new Date(dateString);
    return date
      .toLocaleDateString("en-US", options)
      .replace(/(\d+)(st|nd|rd|th)/, "$1");
  };

  const exportAdmins = (adminsList) => {
    const exportData = adminsList.map((admin) => ({
      Name: admin.name,
      Email: admin.email,
      Status: admin.disabled ? "Disabled" : "Active",
      "Join Date": formatDate(admin.createdAt),
      Plan: admin.currentPlan,
      "Team Size": admin.team || 0,
      "Total Generations": admin.generations || 0,
    }));

    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Admins");

    const fileName = `copilotly_admins_export_${
      new Date().toISOString().split("T")[0]
    }.xlsx`;

    XLSX.writeFile(workbook, fileName);
  };

  const exportToExcel = () => {
    exportAdmins(admins);
  };

  const resetFilters = () => {
    setFilterPlan(null);
    setFilterStatus(null);
    setFilterDateRange(null);
    setPagination({ ...pagination, current: 1 });

    fetchAdmins(1, pagination.pageSize, searchText, sortBy, sortOrder, {});

    const searchParams = new URLSearchParams();
    searchParams.set("page", "1");
    searchParams.set("pageSize", pagination.pageSize);
    if (searchText) searchParams.set("search", searchText);
    if (sortBy) searchParams.set("sortBy", sortBy);
    if (sortOrder) searchParams.set("sortOrder", sortOrder);

    history.push({ search: searchParams.toString() });
  };

  const handleFilter = () => {
    setPagination({ ...pagination, current: 1 });
    fetchAdmins(1, pagination.pageSize, searchText, sortBy, sortOrder, {
      plan: filterPlan,
      status: filterStatus,
      dateRange: filterDateRange,
    });

    const searchParams = new URLSearchParams();
    searchParams.set("page", "1");
    searchParams.set("pageSize", pagination.pageSize);
    if (searchText) searchParams.set("search", searchText);
    if (sortBy) searchParams.set("sortBy", sortBy);
    if (sortOrder) searchParams.set("sortOrder", sortOrder);
    if (filterPlan) searchParams.set("plan", filterPlan);
    if (filterStatus) searchParams.set("status", filterStatus);

    history.push({ search: searchParams.toString() });
  };

  const columns = [
    {
      title: "User",
      key: "user",
      width: 280,
      render: (_, record) => (
        <AdminQuickPreview admin={record}>
          <div className="admin-user-info">
            <div className="admin-user-avatar">
              {(record.name || "U").charAt(0).toUpperCase()}
            </div>
            <div className="admin-user-details">
              <div
                className="admin-user-name"
                onClick={() => handleAdminView(record._id)}
              >
                {record.name}
              </div>
              <div className="admin-user-email">{record.email}</div>
            </div>
          </div>
        </AdminQuickPreview>
      ),
    },
    {
      title: "Status",
      dataIndex: "disabled",
      key: "disabled",
      width: 120,
      render: (disabled, record) => (
        <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
          {disabled ? (
            <Tag className="admin-status-tag disabled">
              <FiUserX size={12} />
              Disabled
            </Tag>
          ) : (
            <Tag className="admin-status-tag active">
              <FiUserCheck size={12} />
              Active
            </Tag>
          )}
          <RiskIndicator admin={record} />
        </div>
      ),
    },
    {
      title: "Join Date",
      dataIndex: "createdAt",
      key: "createdAt",
      width: 140,
      render: (text) => (
        <span style={{ color: "#64748b", fontSize: "13px" }}>
          {formatDate(text)}
        </span>
      ),
    },
    {
      title: "Plan",
      dataIndex: "currentPlan",
      key: "currentPlan",
      width: 110,
      render: (text) => (
        <Tag className={`admin-plan-tag ${(text || "free").toLowerCase()}`}>
          {text || "Free"}
        </Tag>
      ),
    },
    {
      title: "Billing Admin",
      dataIndex: "billingAdmin",
      key: "billingAdmin",
      width: 120,
      render: (billingAdmin) =>
        billingAdmin ? (
          <Tag className="admin-status-tag active">
            <RiVipCrownLine size={12} />
            Yes
          </Tag>
        ) : (
          <Tag className="admin-status-tag pending">No</Tag>
        ),
    },
    {
      title: "Team",
      dataIndex: "team",
      key: "team",
      width: 80,
      render: (team) => (
        <div className="admin-icon-count">
          <FiUsers size={14} color="#7c3aed" />
          <span>{team || 0}</span>
        </div>
      ),
    },
    {
      title: "Generations",
      dataIndex: "generations",
      key: "generations",
      width: 110,
      render: (generations) => (
        <span style={{ fontWeight: 600 }}>{generations?.toLocaleString() || 0}</span>
      ),
    },
    {
      title: "",
      key: "action",
      width: 60,
      fixed: "right",
      render: (_, record) => (
        <Dropdown
          trigger={["click"]}
          overlay={
            <div className="admin-dropdown-menu">
              <button
                className="admin-dropdown-item"
                onClick={() => handleAdminView(record._id)}
              >
                <RiEyeFill size={15} />
                View Details
              </button>
              <div className="admin-dropdown-divider" />
              <button
                className="admin-dropdown-item"
                onClick={() => showChangePlanModal(record._id)}
              >
                <RiBuilding4Line size={15} />
                Change Plan
              </button>
              <button
                className="admin-dropdown-item"
                onClick={() => {
                  Modal.confirm({
                    title: "Impersonate User",
                    icon: <RiSpyLine size={24} color="#8B5CF6" />,
                    content: "You are about to login as this user. This action will be logged.",
                    okText: "Login as User",
                    onOk: () => message.info("Impersonation feature coming soon"),
                  });
                }}
              >
                <RiSpyLine size={15} />
                Impersonate
              </button>
              <button
                className="admin-dropdown-item"
                onClick={() => handleToggleUserDisable(record.user, record.disabled)}
              >
                <MdOutlinePersonAddDisabled size={15} />
                {record.disabled ? "Enable" : "Disable"} Account
              </button>
              <div className="admin-dropdown-divider" />
              <button
                className="admin-dropdown-item danger"
                onClick={() => handleDeleteUserAccount(record.user, record.name)}
              >
                <RiDeleteBin6Line size={15} />
                Delete Account
              </button>
            </div>
          }
          placement="bottomRight"
        >
          <button className="admin-more-btn">
            <FiMoreVertical size={18} />
          </button>
        </Dropdown>
      ),
    },
  ];

  const handleTableChange = (pagination) => {
    handlePaginationChange(pagination.current, pagination.pageSize);
  };

  // Row selection for bulk actions
  const rowSelection = {
    selectedRowKeys,
    onChange: (keys) => setSelectedRowKeys(keys),
    getCheckboxProps: (record) => ({
      name: record.email,
    }),
  };

  const filterMenu = (
    <div className="admin-filter-dropdown">
      <div className="admin-filter-section">
        <div className="admin-filter-label">Plan</div>
        <Select
          style={{ width: "100%" }}
          value={filterPlan}
          onChange={setFilterPlan}
          allowClear
          placeholder="All Plans"
        >
          <Option value="Free">Free</Option>
          <Option value="Optimizer">Optimizer</Option>
          <Option value="Wizard">Wizard</Option>
          <Option value="Maverick">Maverick</Option>
        </Select>
      </div>
      <div className="admin-filter-section">
        <div className="admin-filter-label">Status</div>
        <Select
          style={{ width: "100%" }}
          value={filterStatus}
          onChange={setFilterStatus}
          allowClear
          placeholder="All Status"
        >
          <Option value="active">Active</Option>
          <Option value="disabled">Disabled</Option>
        </Select>
      </div>
      <div className="admin-filter-section">
        <div className="admin-filter-label">Join Date</div>
        <RangePicker
          style={{ width: "100%" }}
          onChange={(dates) => {
            if (dates) {
              setFilterDateRange({
                start: dates[0].startOf("day").toDate(),
                end: dates[1].endOf("day").toDate(),
              });
            } else {
              setFilterDateRange(null);
            }
          }}
        />
      </div>
      <div className="admin-filter-actions">
        <Button className="admin-btn-secondary" onClick={resetFilters}>
          Reset
        </Button>
        <Button className="admin-btn-primary" onClick={handleFilter}>
          Apply
        </Button>
      </div>
    </div>
  );

  // Bulk actions menu
  const bulkActionsMenu = (
    <div className="admin-dropdown-menu" style={{ width: 200 }}>
      <button className="admin-dropdown-item" onClick={() => handleBulkAction("email")}>
        <FiMail size={15} />
        Send Email
      </button>
      <button className="admin-dropdown-item" onClick={() => handleBulkAction("refill")}>
        <FiRefreshCw size={15} />
        Refill Credits
      </button>
      <div className="admin-dropdown-divider" />
      <button className="admin-dropdown-item" onClick={() => handleBulkAction("enable")}>
        <FiUnlock size={15} />
        Enable Accounts
      </button>
      <button className="admin-dropdown-item danger" onClick={() => handleBulkAction("disable")}>
        <FiLock size={15} />
        Disable Accounts
      </button>
      <div className="admin-dropdown-divider" />
      <button className="admin-dropdown-item" onClick={() => handleBulkAction("export")}>
        <FiDownload size={15} />
        Export Selected
      </button>
    </div>
  );

  return (
    <div className="admin-list-container">
      {/* Header */}
      <div className="admin-list-header">
        <div>
          <h1>Admins</h1>
          <p>Manage all registered admin accounts and their subscriptions</p>
        </div>
        <div className="admin-list-actions">
          <Button
            className="admin-btn-secondary"
            icon={<FiRefreshCw size={16} />}
            onClick={() =>
              fetchAdmins(
                pagination.current,
                pagination.pageSize,
                searchText,
                sortBy,
                sortOrder,
                {
                  plan: filterPlan,
                  status: filterStatus,
                  dateRange: filterDateRange,
                }
              )
            }
          >
            Refresh
          </Button>
          <Button
            className="admin-btn-secondary purple"
            icon={<RiCalendarCheckLine size={16} />}
            onClick={openRefillsModal}
          >
            Refills
          </Button>
          <Button
            className="admin-btn-secondary"
            icon={<FiDownload size={16} />}
            onClick={exportToExcel}
          >
            Export
          </Button>
        </div>
      </div>

      {/* Stats Cards - Enhanced with Revenue Metrics */}
      <Row gutter={[16, 16]} className="admin-stats-row">
        <Col xs={12} sm={8} lg={4}>
          <Card className="admin-stat-card">
            <div className="admin-stat-icon blue">
              <FiUsers />
            </div>
            <div className="admin-stat-label">Total Admins</div>
            <div className="admin-stat-value">
              <AnimatedNumber value={stats.total} />
            </div>
          </Card>
        </Col>
        <Col xs={12} sm={8} lg={4}>
          <Card className="admin-stat-card">
            <div className="admin-stat-icon green">
              <FiUserCheck />
            </div>
            <div className="admin-stat-label">Active</div>
            <div className="admin-stat-value green">
              <AnimatedNumber value={stats.active} />
            </div>
          </Card>
        </Col>
        <Col xs={12} sm={8} lg={4}>
          <Card className="admin-stat-card">
            <div className="admin-stat-icon purple">
              <FiTrendingUp />
            </div>
            <div className="admin-stat-label">New This Month</div>
            <div className="admin-stat-value purple">
              <AnimatedNumber value={stats.newThisMonth} />
            </div>
          </Card>
        </Col>
        <Col xs={12} sm={8} lg={4}>
          <Card className="admin-stat-card">
            <div className="admin-stat-icon orange">
              <FiDollarSign />
            </div>
            <div className="admin-stat-label">MRR (Est.)</div>
            <div className="admin-stat-value orange">
              <AnimatedNumber value={stats.mrr} prefix="$" />
            </div>
          </Card>
        </Col>
        <Col xs={12} sm={8} lg={4}>
          <Card className="admin-stat-card">
            <div className="admin-stat-icon pink">
              <FiPercent />
            </div>
            <div className="admin-stat-label">Conversion</div>
            <div className="admin-stat-value pink">
              <AnimatedNumber value={stats.conversionRate} suffix="%" />
            </div>
          </Card>
        </Col>
        <Col xs={12} sm={8} lg={4}>
          <Card className="admin-stat-card">
            <div className="admin-stat-icon red">
              <FiAlertTriangle />
            </div>
            <div className="admin-stat-label">Churn Risk</div>
            <div className="admin-stat-value red">
              <AnimatedNumber value={stats.churnRisk} />
            </div>
          </Card>
        </Col>
      </Row>

      {/* Bulk Actions Bar */}
      {selectedRowKeys.length > 0 && (
        <Alert
          className="admin-bulk-actions-bar"
          message={
            <div className="bulk-actions-content">
              <span className="bulk-selection-count">
                <FiCheckCircle size={16} />
                {selectedRowKeys.length} admin{selectedRowKeys.length > 1 ? "s" : ""} selected
              </span>
              <div className="bulk-actions-buttons">
                <Button
                  size="small"
                  icon={<FiMail size={14} />}
                  onClick={() => handleBulkAction("email")}
                >
                  Email
                </Button>
                <Button
                  size="small"
                  icon={<FiRefreshCw size={14} />}
                  onClick={() => handleBulkAction("refill")}
                >
                  Refill
                </Button>
                <Dropdown overlay={bulkActionsMenu} trigger={["click"]}>
                  <Button size="small" icon={<FiMoreVertical size={14} />}>
                    More
                  </Button>
                </Dropdown>
                <Button
                  size="small"
                  type="text"
                  onClick={() => setSelectedRowKeys([])}
                >
                  Clear
                </Button>
              </div>
            </div>
          }
          type="info"
          showIcon={false}
        />
      )}

      {/* Action Bar */}
      <div className="admin-action-bar">
        <div className="admin-search-wrapper">
          <Input
            className="admin-search-input"
            placeholder="Search by name or email..."
            prefix={<FiSearch size={16} />}
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            onPressEnter={() => handleSearch(searchText)}
            allowClear
            onClear={() => handleSearch("")}
          />
        </div>
        <div className="admin-list-actions">
          <Dropdown
            overlay={filterMenu}
            trigger={["click"]}
            placement="bottomRight"
          >
            <Button className="admin-btn-secondary" icon={<FiFilter size={16} />}>
              Filters
              {(filterPlan || filterStatus || filterDateRange) && (
                <Tag color="purple" style={{ marginLeft: 8, marginRight: 0 }}>
                  {[filterPlan, filterStatus, filterDateRange].filter(Boolean).length}
                </Tag>
              )}
            </Button>
          </Dropdown>
        </div>
      </div>

      {/* Table */}
      <Card className="admin-table-card">
        <Table
          className="admin-table"
          columns={columns}
          dataSource={admins}
          rowSelection={rowSelection}
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: pagination.total,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total) => `Total ${total} admins`,
          }}
          loading={loading}
          onChange={handleTableChange}
          rowKey={(record) => record.user || record._id}
          scroll={{ x: 1200 }}
        />
      </Card>

      {/* Change Plan Modal */}
      <Modal
        className="admin-modal"
        title="Change Subscription Plan"
        visible={changePlanModalVisible}
        onCancel={() => {
          setChangePlanModalVisible(false);
          setSelectedAdminId(null);
          setSelectedPlan(null);
        }}
        footer={[
          <Button
            key="cancel"
            className="admin-btn-secondary"
            onClick={() => {
              setChangePlanModalVisible(false);
              setSelectedAdminId(null);
              setSelectedPlan(null);
            }}
          >
            Cancel
          </Button>,
          <Button
            key="submit"
            className="admin-btn-primary"
            loading={loading}
            disabled={!selectedPlan}
            onClick={handleChangePlan}
          >
            Change Plan
          </Button>,
        ]}
      >
        <div style={{ marginBottom: 16 }}>
          <p style={{ color: "#64748b", marginBottom: 12 }}>
            Select a new subscription plan for this admin:
          </p>
          <Select
            style={{ width: "100%" }}
            placeholder="Select Plan"
            value={selectedPlan}
            onChange={setSelectedPlan}
            size="large"
          >
            {availablePlans.map((plan) => (
              <Option key={plan.id} value={plan.id}>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <div
                    style={{
                      width: 8,
                      height: 8,
                      borderRadius: "50%",
                      backgroundColor: plan.color,
                    }}
                  />
                  {plan.name}
                </div>
              </Option>
            ))}
          </Select>
        </div>
        <Alert
          message="Plan changes take effect immediately"
          description="Changing the plan will update all credits and usage limits for this admin."
          type="info"
          showIcon
        />
      </Modal>

      {/* Bulk Email Modal */}
      <Modal
        className="admin-modal"
        title={
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <FiMail size={20} color="#8B5CF6" />
            Send Email to {selectedRowKeys.length} Admin{selectedRowKeys.length > 1 ? "s" : ""}
          </div>
        }
        visible={bulkEmailModalVisible}
        onCancel={() => setBulkEmailModalVisible(false)}
        footer={[
          <Button
            key="cancel"
            className="admin-btn-secondary"
            onClick={() => setBulkEmailModalVisible(false)}
          >
            Cancel
          </Button>,
          <Button
            key="submit"
            className="admin-btn-primary"
            onClick={() => {
              message.success("Emails queued for sending");
              setBulkEmailModalVisible(false);
            }}
          >
            Send Emails
          </Button>,
        ]}
        width={600}
      >
        <div style={{ marginBottom: 16 }}>
          <label style={{ display: "block", marginBottom: 8, fontWeight: 500 }}>Subject</label>
          <Input placeholder="Enter email subject" />
        </div>
        <div style={{ marginBottom: 16 }}>
          <label style={{ display: "block", marginBottom: 8, fontWeight: 500 }}>Message</label>
          <Input.TextArea rows={6} placeholder="Enter your message..." />
        </div>
        <Alert
          message={`This will send an email to ${selectedRowKeys.length} selected admin${selectedRowKeys.length > 1 ? "s" : ""}`}
          type="info"
          showIcon
        />
      </Modal>

      {/* Refills Modal */}
      <Modal
        className="admin-modal"
        title={
          <div className="admin-modal-title">
            <RiCalendarCheckLine size={20} />
            <span>Subscription Refills Management</span>
          </div>
        }
        visible={refillsModalVisible}
        onCancel={() => setRefillsModalVisible(false)}
        width={1200}
        footer={null}
      >
        {/* Stats Row */}
        {refillsStats && (
          <Row gutter={[16, 16]} style={{ marginBottom: 20 }}>
            <Col span={6}>
              <div className="admin-refill-stats-card">
                <div className="admin-refill-stat-value">
                  <AnimatedNumber value={refillsStats.total} />
                </div>
                <div className="admin-refill-stat-label">Total Users</div>
              </div>
            </Col>
            <Col span={6}>
              <div
                className={`admin-refill-stats-card ${
                  refillsStats.overdue > 0 ? "danger" : "success"
                }`}
              >
                <div
                  className={`admin-refill-stat-value ${
                    refillsStats.overdue > 0 ? "danger" : "success"
                  }`}
                >
                  <AnimatedNumber value={refillsStats.overdue} />
                </div>
                <div className="admin-refill-stat-label">Overdue</div>
              </div>
            </Col>
            <Col span={6}>
              <div
                className={`admin-refill-stats-card ${
                  refillsStats.overdueMoreThan30Days > 0 ? "danger" : "success"
                }`}
              >
                <div
                  className={`admin-refill-stat-value ${
                    refillsStats.overdueMoreThan30Days > 0 ? "danger" : "success"
                  }`}
                >
                  <AnimatedNumber value={refillsStats.overdueMoreThan30Days} />
                </div>
                <div className="admin-refill-stat-label">30+ Days Overdue</div>
              </div>
            </Col>
            <Col span={6}>
              <div className="admin-refill-stats-card success">
                <div className="admin-refill-stat-value success">
                  <AnimatedNumber value={refillsStats.recentlyRefilled} />
                </div>
                <div className="admin-refill-stat-label">Recently Refilled</div>
              </div>
            </Col>
          </Row>
        )}

        {/* Alerts */}
        {refillsStats?.noWorkspace > 0 && (
          <Alert
            type="warning"
            message={`${refillsStats.noWorkspace} users without workspace - will be auto-created on refill`}
            showIcon
            style={{ marginBottom: 16 }}
          />
        )}

        {/* Filters Row */}
        <div className="admin-flex-between" style={{ marginBottom: 16 }}>
          <div className="admin-flex-start">
            <Select
              placeholder="Plan"
              allowClear
              style={{ width: 120 }}
              value={refillsFilters.plan}
              onChange={(value) => {
                setRefillsFilters((prev) => ({ ...prev, plan: value }));
                setRefillsPagination((prev) => ({ ...prev, current: 1 }));
              }}
            >
              <Option value="Free">Free</Option>
              <Option value="Optimizer">Optimizer</Option>
              <Option value="Wizard">Wizard</Option>
              <Option value="Maverick">Maverick</Option>
              <Option value="Lifetime">Lifetime</Option>
            </Select>

            <Button
              className={`admin-btn-secondary ${
                refillsFilters.overdueOnly ? "active" : ""
              }`}
              icon={<FiAlertTriangle size={14} />}
              onClick={() => {
                setRefillsFilters((prev) => ({
                  ...prev,
                  overdueOnly: !prev.overdueOnly,
                }));
                setRefillsPagination((prev) => ({ ...prev, current: 1 }));
              }}
              style={
                refillsFilters.overdueOnly
                  ? { background: "#7c3aed", color: "white", borderColor: "#7c3aed" }
                  : {}
              }
            >
              Overdue
            </Button>

            <Button
              className={`admin-btn-secondary ${
                refillsFilters.recentOnly ? "active" : ""
              }`}
              icon={<FiClock size={14} />}
              onClick={() => {
                setRefillsFilters((prev) => ({
                  ...prev,
                  recentOnly: !prev.recentOnly,
                }));
                setRefillsPagination((prev) => ({ ...prev, current: 1 }));
              }}
              style={
                refillsFilters.recentOnly
                  ? { background: "#7c3aed", color: "white", borderColor: "#7c3aed" }
                  : {}
              }
            >
              Recent
            </Button>

            {selectedRefillKeys.length > 0 && (
              <Button
                className="admin-btn-primary"
                icon={<FiRefreshCw size={14} />}
                onClick={handleBulkRefill}
                style={{ background: "#dc2626", borderColor: "#dc2626" }}
              >
                Bulk Refill ({selectedRefillKeys.length})
              </Button>
            )}
          </div>
          <div className="admin-flex-start">
            <Button
              className="admin-btn-secondary"
              icon={<FiRefreshCw size={14} />}
              onClick={fetchRefillsData}
              loading={refillsLoading}
            >
              Refresh
            </Button>
            <Dropdown
              overlay={
                <Menu>
                  <Menu.Item key="free" onClick={() => handleTriggerJob("free")}>
                    Free Refill Job
                  </Menu.Item>
                  <Menu.Item key="monthly" onClick={() => handleTriggerJob("monthly")}>
                    Monthly Refill Job
                  </Menu.Item>
                  <Menu.Item key="annual" onClick={() => handleTriggerJob("annual")}>
                    Annual Refill Job
                  </Menu.Item>
                  <Menu.Item key="lifetime" onClick={() => handleTriggerJob("lifetime")}>
                    Lifetime Refill Job
                  </Menu.Item>
                </Menu>
              }
              trigger={["click"]}
            >
              <Button className="admin-btn-primary" icon={<FiPlay size={14} />}>
                Run Job
              </Button>
            </Dropdown>
          </div>
        </div>

        {/* Table */}
        <Table
          className="admin-table"
          columns={refillColumns}
          dataSource={refillsData}
          rowKey="adminId"
          loading={refillsLoading}
          rowSelection={refillRowSelection}
          pagination={{
            current: refillsPagination.current,
            pageSize: refillsPagination.pageSize,
            total: refillsPagination.total,
            showSizeChanger: true,
            onChange: (page, pageSize) => {
              setRefillsPagination((prev) => ({
                ...prev,
                current: page,
                pageSize,
              }));
            },
          }}
          scroll={{ y: 400 }}
        />
      </Modal>
    </div>
  );
};

export default AdminList;
